import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  styled,
  Switch,
  Typography,
} from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import React, { useEffect, useState } from 'react'
import { getActionTitle, getConfirmableActions } from './actionPermissions'
import { useActionWhitelist } from './hooks/useActionWhitelist'

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const ScrollableContent = styled(Box)`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: 60vh;
  padding-right: 4px;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;

    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`

const ActionList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`

const ActionItem = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background-color: #ffffff;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
`

const ActionInfo = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const ActionTitle = styled(Typography)`
  font-weight: 500;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
`

const ActionDescription = styled(Typography)`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
`

const EmptyState = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  text-align: center;
`

const EmptyStateText = styled(Typography)`
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
`

const ExplainerBox = styled(Box)`
  padding: 16px;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
`

const ExplainerTitle = styled(Typography)`
  font-weight: 600;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 8px;
`

const ExplainerText = styled(Typography)`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.5;
`

interface ActionWhitelistModalProps {
  open: boolean
  onClose: () => void
}

export const ActionWhitelistModal: React.FC<ActionWhitelistModalProps> = ({ open, onClose }) => {
  const translate = useTranslate()
  const { isActionWhitelisted, toggleWhitelist, whitelistedActions } = useActionWhitelist()
  const [localWhitelist, setLocalWhitelist] = useState<Set<string>>(new Set())

  // Sync local state with hook state when modal opens
  useEffect(() => {
    if (open) {
      setLocalWhitelist(new Set(whitelistedActions))
    }
  }, [open, whitelistedActions])

  const confirmableActions = getConfirmableActions()

  const handleToggle = (actionType: string) => {
    const newWhitelist = new Set(localWhitelist)
    if (newWhitelist.has(actionType)) {
      newWhitelist.delete(actionType)
    } else {
      newWhitelist.add(actionType)
    }
    setLocalWhitelist(newWhitelist)
    toggleWhitelist(actionType)
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('Manage Whitelisted Actions')}</DialogTitle>

      <DialogContent>
        <Container>
          <ExplainerBox style={{ marginBottom: '16px' }}>
            <ExplainerTitle>{translate('How Whitelisted Actions Work')}</ExplainerTitle>
            <ExplainerText>
              {translate(
                'Whitelisted actions are automatically accepted without requiring confirmation. When you whitelist an action, it will execute immediately when requested by Ada, both in text chat and voice mode. This is useful for frequently used actions like running auto-design. You can toggle actions on or off at any time.'
              )}
            </ExplainerText>
          </ExplainerBox>
          <ScrollableContent>
            {confirmableActions.length === 0 ? (
              <EmptyState>
                <EmptyStateText>{translate('No actions require confirmation.')}</EmptyStateText>
              </EmptyState>
            ) : (
              <ActionList>
                {confirmableActions.map(({ actionType, config }) => {
                  const isWhitelisted = localWhitelist.has(actionType)
                  const actionTitle = getActionTitle(actionType) || actionType
                  const description = config.description || translate('No description available.')

                  return (
                    <ActionItem key={actionType}>
                      <ActionInfo>
                        <ActionTitle>{translate(actionTitle)}</ActionTitle>
                        <ActionDescription>{translate(description)}</ActionDescription>
                      </ActionInfo>
                      <FormControlLabel
                        control={
                          <Switch checked={isWhitelisted} onChange={() => handleToggle(actionType)} size="small" />
                        }
                        label=""
                      />
                    </ActionItem>
                  )
                })}
              </ActionList>
            )}
          </ScrollableContent>
        </Container>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{translate('Close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
